import { faro } from '@grafana/faro-react'
import { BreakoutRoom } from 'api/classroom/ClassroomState'
import {
    BreakoutHealthSnapshot,
    snapshotToMismatchAttributes
} from 'services/breakoutHealthCheck'

export type BreakoutFaroEvent =
    | 'breakout_started'
    | 'breakout_started_cue_cards'
    | 'breakout_stopped'
    | 'local_audio'
    | 'local_audio_applied'
    | 'local_audio_error'
    | 'room_assignment_changed'
    | 'breakout_refresh_isolation'
    | 'breakout_health_mismatch'
    | 'breakout_rejoin_attempted'
    | 'breakout_isolation_applied'
    | 'breakout_teacher_link_changed'
    | 'local_audio_leak_detected'
    | 'breakout_sync_rooms'

const dedupCache = new Set<string>()
const appliedDedupCache = new Set<string>()
const leakDedupCache = new Set<string>()
let healthTickSequence = 0
let breakoutSessionId = ''
let cachedClassId: string | null = null
let cachedViewerClientUserId: string | null = null

export const isBreakoutFaroLoggingEnabled = (): boolean => {
    return process.env.REACT_APP_FARO_BREAKOUT_LOGGING === 'true'
}

export const setBreakoutFaroClassId = (classId: string): void => {
    cachedClassId = classId
}

export const resetBreakoutFaroClassId = (): void => {
    cachedClassId = null
}

export const setBreakoutFaroViewerClientUserId = (clientUserId: string): void => {
    cachedViewerClientUserId = clientUserId
}

export const resetBreakoutFaroViewerClientUserId = (): void => {
    cachedViewerClientUserId = null
}

const getClassId = (): string => {
    if (typeof sessionStorage !== 'undefined') {
        const fromStorage = sessionStorage.getItem('classroomId')
        if (fromStorage) {
            return fromStorage
        }
    }
    return cachedClassId ?? 'unknown'
}

const getViewerUserId = (): string => {
    if (typeof sessionStorage === 'undefined') {
        return 'unknown'
    }
    return sessionStorage.getItem('userId') ?? 'unknown'
}

const getViewerClientUserId = (): string => cachedViewerClientUserId ?? 'unknown'

export const logBreakoutEvent = (
    eventName: BreakoutFaroEvent,
    attributes: Record<string, string | number | boolean> = {}
): void => {
    if (!isBreakoutFaroLoggingEnabled() || !faro.api?.pushEvent) {
        return
    }

    if (eventName === 'breakout_started' && !breakoutSessionId) {
        startBreakoutFaroSession()
    }

    const stringAttributes: Record<string, string> = {}
    for (const [key, value] of Object.entries({
        ...attributes,
        viewerUserId: getViewerUserId(),
        viewerClientUserId: getViewerClientUserId(),
        timestamp: new Date().toISOString(),
        breakoutSessionId,
        classId: getClassId()
    })) {
        stringAttributes[key] = String(value)
    }

    faro.api.pushEvent(eventName, stringAttributes)
}

export const startBreakoutFaroSession = (): string => {
    breakoutSessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`
    healthTickSequence = 0
    return breakoutSessionId
}

export const getBreakoutSessionId = (): string => breakoutSessionId

export const nextHealthTickSequence = (): number => {
    healthTickSequence += 1
    return healthTickSequence
}

export const logBreakoutPeriodicHealth = (snapshot: BreakoutHealthSnapshot): void => {
    if (!isBreakoutFaroLoggingEnabled()) {
        return
    }

    if (!breakoutSessionId) {
        startBreakoutFaroSession()
    }

    if (snapshot.hasMismatch) {
        logBreakoutEvent('breakout_health_mismatch', snapshotToMismatchAttributes(snapshot))
    }
}

export interface LocalAudioParams {
    action: 'mute' | 'unmute' | 'exclude'
    targetUserId: string
    clientUserId: string
    targetRoom: number
    viewerRoom: number | null
    viewerRole: string
    source: 'isolation' | 'layout'
    roomAssignments?: string
    zoomLiveParticipantIds?: string
    zoomLiveParticipantClientIds?: string
    usedZoomLiveParticipants?: boolean
}

export interface LocalAudioAppliedParams {
    action: 'mute' | 'unmute'
    targetUserId: string
    clientUserId: string
}

export interface LocalAudioErrorParams {
    action: 'mute' | 'unmute'
    targetUserId: string
    clientUserId: string
    errorCode?: number | string
    errorType?: string
    reason?: string
}

export const logLocalAudioApplied = (params: LocalAudioAppliedParams): void => {
    if (!isBreakoutFaroLoggingEnabled()) {
        return
    }

    const dedupKey = `${params.targetUserId}:${params.action}:applied`
    if (appliedDedupCache.has(dedupKey)) {
        return
    }
    appliedDedupCache.add(dedupKey)

    logBreakoutEvent('local_audio_applied', {
        action: params.action,
        targetUserId: params.targetUserId,
        clientUserId: params.clientUserId
    })
}

export const logLocalAudioError = (params: LocalAudioErrorParams): void => {
    if (!isBreakoutFaroLoggingEnabled()) {
        return
    }

    logBreakoutEvent('local_audio_error', {
        action: params.action,
        targetUserId: params.targetUserId,
        clientUserId: params.clientUserId,
        errorCode: params.errorCode ?? -1,
        errorType: params.errorType ?? 'unknown',
        reason: params.reason ?? 'unknown'
    })
}

export interface LocalAudioLeakDetectedParams {
    targetUserId: string
    targetClientUserId: string
    targetRoom: number
    viewerRoom: number | null
    viewerRole: string
}

export const logLocalAudioLeakDetected = (params: LocalAudioLeakDetectedParams): void => {
    if (!isBreakoutFaroLoggingEnabled()) {
        return
    }

    const dedupKey = `${params.targetUserId}:leak`
    if (leakDedupCache.has(dedupKey)) {
        return
    }
    leakDedupCache.add(dedupKey)

    logBreakoutEvent('local_audio_leak_detected', {
        targetUserId: params.targetUserId,
        targetClientUserId: params.targetClientUserId,
        targetRoom: params.targetRoom,
        viewerRoom: params.viewerRoom ?? -1,
        viewerRole: params.viewerRole
    })
}

export const logLocalAudio = (params: LocalAudioParams): void => {
    if (!isBreakoutFaroLoggingEnabled()) {
        return
    }

    if (params.source === 'layout') {
        const dedupKey = `${params.targetUserId}:${params.action}:${String(params.viewerRoom)}`
        if (dedupCache.has(dedupKey)) {
            return
        }
        dedupCache.add(dedupKey)
    }

    logBreakoutEvent('local_audio', {
        action: params.action,
        targetUserId: params.targetUserId,
        clientUserId: params.clientUserId,
        targetRoom: params.targetRoom,
        viewerRoom: params.viewerRoom ?? -1,
        viewerRole: params.viewerRole,
        source: params.source,
        ...(params.roomAssignments !== undefined && { roomAssignments: params.roomAssignments }),
        ...(params.zoomLiveParticipantIds !== undefined && { zoomLiveParticipantIds: params.zoomLiveParticipantIds }),
        ...(params.zoomLiveParticipantClientIds !== undefined && { zoomLiveParticipantClientIds: params.zoomLiveParticipantClientIds }),
        ...(params.usedZoomLiveParticipants !== undefined && { usedZoomLiveParticipants: params.usedZoomLiveParticipants })
    })
}

const resetHealthLoggingState = (): void => {
    healthTickSequence = 0
    breakoutSessionId = ''
}

export const clearBreakoutFaroDedup = (): void => {
    dedupCache.clear()
    appliedDedupCache.clear()
    leakDedupCache.clear()
    resetHealthLoggingState()
}

export const formatRoomAssignments = (rooms: BreakoutRoom[]): string => {
    const assignments: Record<number, string[]> = {}
    rooms.forEach(room => {
        assignments[room.id] = room.users
    })
    return JSON.stringify(assignments)
}
